import React, { useState, useEffect, useMemo } from 'react';
import EngagerSliderRow from './calc_components/EngagerSliderRow';
import CalculatorToolsRail from '../components/CalculatorToolsRail';
import { inrAmountInWordsShort } from './calc_components/calculatorAmountWords';
import CalculatorWhatThisMeans from './calc_components/CalculatorWhatThisMeans';
import CalculatorPrecisionToggle from './calc_components/CalculatorPrecisionToggle';
import { useCalcAmountDisplayMode } from './calc_components/useCalcAmountDisplayMode';
import { formatYearsCount } from './calc_components/calculatorFormatters';
import { computeEmi } from './EmiCalculator';
import './calculators.css';
import './PreEmiCalculator.css';

const formatterINR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const minInterestRate = 6;
const maxInterestRate = 50;
const interestRateStep = 0.25;

const minTenureYears = 5;
const maxTenureYears = 30;

const minLoanAmount = 0;
const maxLoanAmount = 1000000000;

const minDisbursementMonth = 1;
const absoluteMaxDisbursementMonth = 36;

/** Matches the ₹1 crore worked example in the Pre-EMI article. */
const STANDALONE_DEFAULTS = {
  interestRate: 9,
  sanctionedLoan: 10000000,
  tenureYears: 20,
  initialDisbursement: 3000000,
  finalDisbursementMonth: 24,
};

const ARTICLE_EMBED_DEFAULTS = { ...STANDALONE_DEFAULTS };

function getDefaults(embed) {
  return embed ? ARTICLE_EMBED_DEFAULTS : STANDALONE_DEFAULTS;
}

function formatInterestRatePct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}%`;
}

function snapInterestRate(n) {
  const raw = Number(n);
  if (!Number.isFinite(raw)) return STANDALONE_DEFAULTS.interestRate;
  const snapped =
    minInterestRate +
    Math.round((raw - minInterestRate) / interestRateStep) * interestRateStep;
  return Math.min(maxInterestRate, Math.max(minInterestRate, Number(snapped.toFixed(2))));
}

function formatMonthsCount(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `${n} ${n === 1 ? 'month' : 'months'}`;
}

/**
 * Compare full-EMI-from-day-one vs pre-EMI until final disbursement.
 *
 * Timing: final disbursement month M means the remaining principal is released
 * at the *end* of month M. So months 1..M are charged only on the initial
 * disbursement; the first month that includes the remaining principal is M+1.
 */
export function computePreEmiComparison({
  interestRate,
  sanctionedLoan,
  tenureYears,
  initialDisbursement,
  finalDisbursementMonth,
}) {
  const P = Math.max(0, Number(sanctionedLoan) || 0);
  const P0 = Math.min(P, Math.max(0, Number(initialDisbursement) || 0));
  const years = Math.max(0, Number(tenureYears) || 0);
  const M = Math.max(0, Math.round(Number(finalDisbursementMonth) || 0));
  const annualPct = Number(interestRate) || 0;
  const n = Math.round(years * 12);
  const r = annualPct / 12 / 100;
  const remainingDisbursement = Math.max(0, P - P0);
  const emi = computeEmi(P, annualPct, years);

  const empty = {
    emi: 0,
    fullEmiInterest: 0,
    preEmiInterest: 0,
    extraInterest: 0,
    fullEmiMonths: 0,
    preEmiMonths: 0,
    fullEmiTotalPaid: 0,
    preEmiTotalPaid: 0,
  };

  if (P <= 0 || years <= 0 || n <= 0 || P0 <= 0) {
    return empty;
  }

  // --- Full EMI from day one ---
  // Months 1..M: EMI on initial disbursement only.
  // End of month M: remaining principal is released.
  // Months M+1..: EMI on the combined outstanding.
  let outstanding = P0;
  let fullInterest = 0;
  let fullPaid = 0;
  let fullMonths = 0;
  const maxMonths = n + M + 2400;

  for (let m = 1; m <= M; m++) {
    if (outstanding > 0.5) {
      const interest = r > 0 ? outstanding * r : 0;
      let payment = emi;
      let principal = payment - interest;
      if (principal < 0) {
        payment = interest;
        principal = 0;
      }
      if (principal > outstanding) {
        principal = outstanding;
        payment = interest + principal;
      }
      outstanding -= principal;
      fullInterest += interest;
      fullPaid += payment;
    }
    fullMonths += 1;
  }

  // Final disbursement at the end of month M (after that month's EMI).
  outstanding += remainingDisbursement;

  while (outstanding > 0.5 && fullMonths < maxMonths) {
    const interest = r > 0 ? outstanding * r : 0;
    let payment = emi;
    let principal = payment - interest;
    if (principal < 0) break;
    if (principal > outstanding) {
      principal = outstanding;
      payment = interest + principal;
    }
    outstanding -= principal;
    fullInterest += interest;
    fullPaid += payment;
    fullMonths += 1;
  }

  // --- Pre-EMI for months 1..M (interest on initial only), then full EMI ---
  // Remaining principal is released at the end of month M; full EMI starts in M+1.
  let preInterest = 0;
  let prePaid = 0;
  for (let m = 1; m <= M; m++) {
    const interest = r > 0 ? P0 * r : 0;
    preInterest += interest;
    prePaid += interest;
  }

  outstanding = P;
  let postMonths = 0;
  for (let m = 1; m <= n && outstanding > 0.5; m++) {
    const interest = r > 0 ? outstanding * r : 0;
    let payment = emi;
    let principal = payment - interest;
    if (principal < 0) break;
    if (principal > outstanding) {
      principal = outstanding;
      payment = interest + principal;
    }
    outstanding -= principal;
    preInterest += interest;
    prePaid += payment;
    postMonths += 1;
  }

  const extraInterest = Math.max(0, preInterest - fullInterest);

  return {
    emi,
    fullEmiInterest: fullInterest,
    preEmiInterest: preInterest,
    extraInterest,
    fullEmiMonths: fullMonths,
    preEmiMonths: M + postMonths,
    fullEmiTotalPaid: fullPaid,
    preEmiTotalPaid: prePaid,
  };
}

function MoneyField({
  id,
  label,
  tooltip,
  value,
  setValue,
  wordsValue,
  setWordsValue,
  max = maxLoanAmount,
  step = 100000,
  defaultValue,
}) {
  const words =
    wordsValue > 0 ? inrAmountInWordsShort(Math.round(wordsValue)) : '';

  return (
    <div className="savings-goal-first-year-exp-field">
      <EngagerSliderRow
        id={id}
        label={label}
        tooltip={tooltip}
        value={Math.min(value, max)}
        setValue={(v) => {
          const next = Math.min(max, Math.max(0, v));
          setValue(next);
          setWordsValue(next);
        }}
        onLiveValueChange={(n) => {
          if (n != null && Number.isFinite(n)) {
            setWordsValue(Math.min(max, Math.max(0, n)));
          } else {
            setWordsValue(0);
          }
        }}
        min={minLoanAmount}
        max={max}
        step={step}
        format={(v) => formatterINR.format(v)}
        variant="inr"
        defaultValue={defaultValue}
      />
      {words ? (
        <p className="calc-output-amount-in-words savings-goal-first-year-exp-words">
          {words}
        </p>
      ) : null}
    </div>
  );
}

export default function PreEmiCalculator({ embed = false }) {
  useEffect(() => {
    if (!embed) window.scrollTo(0, 0);
  }, [embed]);

  const defaults = getDefaults(embed);
  const [interestRate, setInterestRate] = useState(defaults.interestRate);
  const [sanctionedLoan, setSanctionedLoan] = useState(defaults.sanctionedLoan);
  const [tenureYears, setTenureYears] = useState(defaults.tenureYears);
  const [initialDisbursement, setInitialDisbursement] = useState(
    defaults.initialDisbursement,
  );
  const [finalDisbursementMonth, setFinalDisbursementMonth] = useState(
    defaults.finalDisbursementMonth,
  );
  const [sanctionedForWords, setSanctionedForWords] = useState(defaults.sanctionedLoan);
  const [initialForWords, setInitialForWords] = useState(defaults.initialDisbursement);

  const clampedInitial = Math.min(initialDisbursement, sanctionedLoan);
  const maxFinalDisbursementMonth = Math.max(
    minDisbursementMonth,
    Math.min(absoluteMaxDisbursementMonth, Math.round(tenureYears * 12)),
  );

  useEffect(() => {
    if (finalDisbursementMonth > maxFinalDisbursementMonth) {
      setFinalDisbursementMonth(maxFinalDisbursementMonth);
    }
  }, [finalDisbursementMonth, maxFinalDisbursementMonth]);

  const result = useMemo(
    () =>
      computePreEmiComparison({
        interestRate,
        sanctionedLoan,
        tenureYears,
        initialDisbursement: clampedInitial,
        finalDisbursementMonth: Math.min(
          finalDisbursementMonth,
          maxFinalDisbursementMonth,
        ),
      }),
    [
      interestRate,
      sanctionedLoan,
      tenureYears,
      clampedInitial,
      finalDisbursementMonth,
      maxFinalDisbursementMonth,
    ],
  );

  function handleStartOver(e) {
    e.preventDefault();
    const d = getDefaults(embed);
    setInterestRate(d.interestRate);
    setSanctionedLoan(d.sanctionedLoan);
    setTenureYears(d.tenureYears);
    setInitialDisbursement(d.initialDisbursement);
    setFinalDisbursementMonth(d.finalDisbursementMonth);
    setSanctionedForWords(d.sanctionedLoan);
    setInitialForWords(d.initialDisbursement);
  }

  const calculatorCard = (
    <div
      className={`appContainer nps-ups--compact emi-calculator pre-emi-calculator${
        embed ? ' nps-ups--embed' : ''
      }`}
    >
      <h1 className="tool-heading">Pre-EMI Cost Calculator</h1>

      <div className="col-light-grey italic font-size-small nps-ups-intro">
        <p>
          See how much extra interest pre-EMI can cost versus paying full EMI from day one on
          an under-construction loan.
        </p>
      </div>

      <PreEmiResultPanel
        extraInterest={result.extraInterest}
        result={result}
      />

      <div className="border-top-light nps-ups-divider" />

      <section
        className="pre-emi-section pre-emi-section--band-b"
        aria-labelledby="pre-emi-sec-loan"
      >
        <h3 className="pre-emi-section-heading" id="pre-emi-sec-loan">
          Loan details
        </h3>
        <div className="pre-emi-inputs">
          <EngagerSliderRow
            id="pre-emi-interest-rate"
            label="Interest rate"
            tooltip="Annual floating home-loan rate used for both the pre-EMI and full-EMI paths."
            value={interestRate}
            setValue={(v) => setInterestRate(snapInterestRate(v))}
            min={minInterestRate}
            max={maxInterestRate}
            step={interestRateStep}
            format={formatInterestRatePct}
            variant="inr"
            defaultValue={defaults.interestRate}
          />

          <MoneyField
            id="pre-emi-sanctioned"
            label="Sanctioned loan"
            tooltip="Total loan amount sanctioned by the lender."
            value={sanctionedLoan}
            setValue={(v) => {
              setSanctionedLoan(v);
              if (initialDisbursement > v) setInitialDisbursement(v);
              if (initialForWords > v) setInitialForWords(v);
            }}
            wordsValue={sanctionedForWords}
            setWordsValue={setSanctionedForWords}
            defaultValue={defaults.sanctionedLoan}
          />

          <EngagerSliderRow
            id="pre-emi-tenure"
            label="Loan tenure (years)"
            tooltip="Contracted repayment tenure used to set the EMI on the full sanctioned loan."
            value={tenureYears}
            setValue={setTenureYears}
            min={minTenureYears}
            max={maxTenureYears}
            step={1}
            format={formatYearsCount}
            variant="inr"
            defaultValue={defaults.tenureYears}
          />
        </div>
      </section>

      <section
        className="pre-emi-section pre-emi-section--band-b"
        aria-labelledby="pre-emi-sec-disbursement"
      >
        <h3 className="pre-emi-section-heading" id="pre-emi-sec-disbursement">
          Disbursement schedule
        </h3>
        <div className="pre-emi-inputs">
          <MoneyField
            id="pre-emi-initial"
            label="Initial disbursement"
            tooltip="Amount disbursed at the start (before the remaining loan is released)."
            value={clampedInitial}
            setValue={setInitialDisbursement}
            wordsValue={Math.min(initialForWords, sanctionedLoan)}
            setWordsValue={setInitialForWords}
            max={Math.max(sanctionedLoan, 1)}
            defaultValue={defaults.initialDisbursement}
          />

          <EngagerSliderRow
            id="pre-emi-final-month"
            label="Final disbursement month"
            tooltip="The remaining sanctioned amount is disbursed at the end of the selected month. For example, if you choose Month 24, the balance will be released at the end of the second year. You will therefore pay only the interest on the initial disbursement for the first 24 months. The full EMI will begin from Month 25."
            value={Math.min(finalDisbursementMonth, maxFinalDisbursementMonth)}
            setValue={setFinalDisbursementMonth}
            min={minDisbursementMonth}
            max={maxFinalDisbursementMonth}
            step={1}
            format={formatMonthsCount}
            variant="inr"
            defaultValue={defaults.finalDisbursementMonth}
          />
        </div>
      </section>

      <button type="button" className="calc-start-over-btn" onClick={handleStartOver}>
        Start over
      </button>

      <p className="font-small font-light-grey italic margin-0 nps-ups-footnote">
        Illustrative model: full EMI is calculated on the sanctioned loan; interest before final
        disbursement is charged only on the amount disbursed. Final disbursement is assumed at
        the end of the chosen month. Excludes fees, insurance, rate resets, and tax effects. Not
        lender advice.
      </p>
    </div>
  );

  if (embed) return calculatorCard;

  return (
    <div className="calculator-page-layout">
      {calculatorCard}
      <div className="calculator-rail-shell">
        <CalculatorToolsRail currentToolKey="pre-emi-calculator" />
      </div>
    </div>
  );
}

function PreEmiResultPanel({ extraInterest, result }) {
  const [showResult, setShowResult] = useState(true);
  const { formatInr, resolveAmount } = useCalcAmountDisplayMode();
  const extraDisplay = formatInr(extraInterest);
  const wordsLine =
    Number.isFinite(extraInterest) && extraInterest > 0
      ? inrAmountInWordsShort(resolveAmount(extraInterest))
      : '';

  return (
    <div className="grid-center-display-result nps-ups-result-panel nps-ups-result-outer calc-result-legacy nps-result-legacy">
      <CalculatorPrecisionToggle />
      <button
        type="button"
        className="btn-small-round nps-ups-result-help"
        onClick={() => setShowResult((s) => !s)}
        aria-label={showResult ? 'Show explanation' : 'Show number'}
      >
        {showResult ? '?' : '×'}
      </button>

      {showResult ? (
        <>
          <h4>Extra interest from choosing pre-EMI</h4>
          <h1 className="nps-ups-years-figure engager-money-figure">{extraDisplay}</h1>
          {wordsLine ? (
            <p className="calc-output-amount-in-words">{wordsLine}</p>
          ) : null}
          <p className="font-small font-light-grey italic margin-0">
            vs paying full EMI from day one · EMI {formatInr(result.emi || 0)}
          </p>
        </>
      ) : (
        <>
          <h4 className="calc-wtm-heading">What this means</h4>
          <CalculatorWhatThisMeans
            regionLabel="Explanation of your Pre-EMI result"
            heroValue={extraDisplay}
            heroVariant="inr"
            tagline={
              <p>
                Extra interest if you choose <strong>pre-EMI</strong> until final disbursement
                instead of paying the <em>full EMI from day one</em>.
              </p>
            }
            highlight={
              <>
                Pre-EMI interest ~{formatInr(result.preEmiInterest || 0)}
                {' · '}
                Full-EMI interest ~{formatInr(result.fullEmiInterest || 0)}
              </>
            }
            highlightTone="warning"
            foot="Illustrative only—not advice."
          />
        </>
      )}
    </div>
  );
}
